import { Input, Select } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import styles from './CompanyManualEntry.module.css';
import { companyManualEntrySchema } from './CompanyManualEntry.schema';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { TMobileSignupForm } from '../../../form/form';
import { useStageFieldTracking } from '../../../form/MobileSignupFieldTracking';
import { useScreenValidation } from '../../../form/validation/useScreenValidation';
import { StepLayout } from '../../../StepLayout/StepLayout';
import { DE_COMPANY_TYPE } from '../companyData';

// DIT-682. Manual company entry. Reachable two ways: the user picks "not listed"
// on the search screen, or the legal-form lookup fails. Registers directly
// against the shared mobile-signup form store.
export const CompanyManualEntry = ({
  onBackToSearch,
}: {
  onBackToSearch: () => void;
}) => {
  const { t } = useLingui();
  const {
    register,
    formState: { errors },
  } = useFormContext<TMobileSignupForm>();
  const { validateAndContinue } = useScreenValidation(companyManualEntrySchema);
  const { fieldProps } = useStageFieldTracking();

  return (
    <StepLayout
      stageTitle={t`Business details`}
      heading={t`Enter your company details`}
      onContinue={validateAndContinue}
      fieldProps={fieldProps}
    >
      <div className={styles.fields}>
        <ButtonLink
          type="button"
          className={styles.backToSearch}
          onClick={onBackToSearch}
        >
          {`← ${t`Go back to search your company`}`}
        </ButtonLink>
        <Input
          {...register('companyName')}
          label={t`Limited company`}
          placeholder={t`e.g. Company Name`}
          className="fs-mask"
          error={errors.companyName?.message}
        />
        <Input
          {...register('street')}
          label={t`Street`}
          placeholder={t`e.g. Street`}
          className="fs-mask"
          error={errors.street?.message}
        />
        <div className={styles.row}>
          <Input
            {...register('buildingNumber')}
            label={t`House number`}
            placeholder={t`e.g. 21`}
            inputMode="numeric"
            className="fs-mask"
            error={errors.buildingNumber?.message}
          />
          <Input
            {...register('postcode')}
            label={t`Postcode`}
            placeholder={t`e.g. 37269`}
            inputMode="numeric"
            maxLength={5}
            className="fs-mask"
            error={errors.postcode?.message}
          />
        </div>
        <Input
          {...register('town')}
          label={t`Town`}
          placeholder={t`e.g. London`}
          className="fs-mask"
          error={errors.town?.message}
        />
        <Select
          {...register('legalForm')}
          label={t`What legal form does your company have?`}
          placeholder={t`Please select`}
          error={errors.legalForm?.message}
        >
          {DE_COMPANY_TYPE.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </div>
    </StepLayout>
  );
};
